import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Button
} from '@mui/material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

// Components
import CreditChip from './CreditChip.js';
import TableHeader from './TableHeader.js';
import CourseRow from './CourseRow.js';

// Utils
import { getOrdinal } from '../utils/dateUtils.js';
import { getPeriodChipColor } from '../utils/colorUtils.js';
import { CREDIT_LIMITS, UI_CONSTANTS } from '../utils/constants.js';
import { isCourseDisabled } from '../utils/validationUtils.js';

/**
 * PeriodTable component for displaying courses in a specific period
 * @param {string} groupKey - Unique key for this period group
 * @param {Object} group - Group data containing courses and metadata
 * @param {number} periodCredits - Credits for this period
 * @param {boolean} isMinimized - Whether the table is minimized
 * @param {Function} onToggleMinimize - Function to toggle minimize state
 * @param {Object} sensors - Drag and drop sensors
 * @param {Function} onDragEnd - Function to handle drag end
 * @param {Set} selectedIds - Set of selected course IDs
 * @param {Set} backupIds - Set of backup course IDs
 * @param {Set} selectedCourses - Set of selected course codes
 * @param {Function} onToggleSelect - Function to handle course selection
 * @param {Function} onAddCourse - Function to open add course dialog
 * @param {Object} groupedCourses - All grouped courses
 * @param {Object} semesterCredits - Credits per semester
 */
const PeriodTable = ({
  groupKey,
  group,
  periodCredits,
  isMinimized,
  onToggleMinimize,
  sensors,
  onDragEnd,
  selectedIds,
  backupIds,
  selectedCourses,
  onToggleSelect,
  onAddCourse,
  groupedCourses,
  semesterCredits
}) => {
  return (
    <Paper key={groupKey} elevation={3} sx={{ p: 3, mb: 1 }}>
      <Box 
        display="flex" 
        alignItems="center" 
        gap={2} 
        mb={isMinimized ? 0 : 1}
        onClick={() => onToggleMinimize(groupKey)}
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          },
          borderRadius: 1,
          p: 1,
          mx: -1
        }}
      >
        <Typography variant="h6">
          {getOrdinal(group.period)} Period {isMinimized ? UI_CONSTANTS.COLLAPSE_ICON : UI_CONSTANTS.EXPAND_ICON}
        </Typography>
        <CreditChip
          credits={periodCredits}
          maxCredits={CREDIT_LIMITS.PERIOD_OPTIMAL}
          type="period"
          getColorFn={getPeriodChipColor}
          variant={periodCredits > 0 ? 'filled' : 'outlined'}
          size="small"
        />
      </Box>
      
      {!isMinimized && (
        <TableContainer component={Paper} variant="outlined">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => onDragEnd(e, groupKey)}
          >
            <SortableContext
              items={group.courses.map((course) => 
                course.is_split_course 
                  ? `${course.semester_instance}_p${course.actual_period}` 
                  : course.semester_instance
              )}
              strategy={verticalListSortingStrategy}
            >
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableHeader />
                <TableBody>
                  {group.courses.map((course) => {
                    const courseId = course.is_split_course 
                      ? `${course.semester_instance}_p${course.actual_period}` 
                      : course.semester_instance;
                    
                    return (
                      <CourseRow
                        key={courseId}
                        id={courseId}
                        course={course}
                        selectedIds={selectedIds}
                        backupIds={backupIds}
                        onToggleSelect={onToggleSelect}
                        isDisabled={isCourseDisabled(
                          course, 
                          groupKey, 
                          groupedCourses, 
                          selectedIds, 
                          backupIds, 
                          selectedCourses, 
                          semesterCredits
                        )}
                        selectedCourses={selectedCourses}
                        groupedCourses={groupedCourses}
                      />
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={4} style={{ textAlign: 'center', padding: '8px' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onAddCourse(group.year, group.semester, group.period)}
                        sx={{ 
                          borderColor: 'grey.400',
                          color: 'grey.600',
                          '&:hover': {
                            borderColor: 'primary.main',
                            color: 'primary.main'
                          }
                        }}
                      >
                        + Add Other Course
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </SortableContext>
          </DndContext>
        </TableContainer>
      )}
    </Paper>
  );
};

export default PeriodTable; 